import { createSlice } from "@reduxjs/toolkit";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import { Auth0Client } from '@auth0/auth0-spa-js';
import axios from "axios";

import {
  doc,
  collection,
  setDoc,
  getDocs,
  getDoc,
  deleteDoc 
} from "firebase/firestore";
import { db } from "../../services/firebase";
import { environment } from "../../environment";

export enum LocalStorageKeys {
  userToken = "userToken",
}
const initialState = {
  users: [],
  fetchingUsers: false,
  selectedShop:null,
  userStripeSecretKey:null,
  subscriptionId:null,
  isLoading: false,
  isCreateUserError: false,
  isSuccess: false,
  userNotifsConfig: null
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUsers: (state, action) => {
      state.users = action.payload;
    },
    setFetchingUsers: (state, action) => {
      state.fetchingUsers = action.payload;
    },
    setSelectedShop: (state, action) => {
      state.selectedShop = action.payload;
    },
    setUserStripeSecretKey: (state, action) => {
      state.userStripeSecretKey = action.payload;
    },
    setSubscriptionId: (state, action) => {
      state.subscriptionId = action.payload
        },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload
    },
    setIsCreateUserError: (state, action) => {
      state.isCreateUserError = action.payload
    },
    setIsSuccess: (state, action) => {
      state.isSuccess = action.payload
    },
    setUserNotifsConfig: (state, action) => {
      state.userNotifsConfig = action.payload
    }
  },
});

const AUTH0_DOMAIN = "dev-nt0zjo2frfiyoocn.us.auth0.com"; // e.g., "yourdomain.auth0.com"
const CLIENT_ID = "aEiTKrD8Q2gYw6vBcjGMfym5g960WQVQ";
const CLIENT_SECRET = "e1C3oKIrWM3NzPeOfC9iM5x_gkws-2a8i8oCUKQvTFimQESCwAuaIC6g3CYOmDzn";

export const axiosInstance = axios.create({
  baseURL: `https://${AUTH0_DOMAIN}/api/v2/`,
  headers: {
    "Content-Type": "application/json",
  },
});

export const getToken = async () =>{
  try {
    const response = await axios.post(`https://${AUTH0_DOMAIN}/oauth/token`, {
      client_id: CLIENT_ID,
      client_secret: CLIENT_SECRET,
      audience: `https://${AUTH0_DOMAIN}/api/v2/`,
      grant_type: "client_credentials",
    });

    const token = response.data.access_token;
    axiosInstance.defaults.headers.Authorization = `Bearer ${token}`;
    return token;
  } catch (error: any) {
    console.error("Failed to fetch token:", error.response?.data || error.message);
    throw new Error("Could not authenticate with Auth0");
  }
}

export const createPaymentIntent = (fullName,email, id, selectedPlan) => async (dispatch: Function) => {
  try {
    const res = await axiosInstance.post(`${environment.serverURL}/payments/create-payment-intent`, {fullName, email, id, plan: selectedPlan})
    if (res.status === 200) {
      dispatch(setUserStripeSecretKey(res.data.clientSecret));
      dispatch(setSubscriptionId(res.data.subscriptionId));
    }
  } catch (error) {
    console.error(error);
  }
};

// export const getAllUsers = () => async (dispatch: Function) => {
//   try {
//     const userRef = collection(db, "users");
//     const users = await getDocs(userRef);
//     const formattedUsers = users.docs.map((doc) => {
//       return { id: doc.id, ...doc.data() };
//     });
//     dispatch(setUsers(formattedUsers));
//   } catch (error) {
//     console.error(error);
//   }
// };

export const deleteUserById = (user: any) => async (dispatch: Function) => {
  try {
    const auth = getAuth();
    const userRef = doc(db, "users", user.id);
    await deleteDoc(userRef);
    // deleteUser(user.id);
    await new Promise((r) => setTimeout(r, 1000));
    dispatch(getAllUsers());
  } catch (error) {
    console.error(error);
  }
};

export const getAllUsers = () => async (dispatch: Function) => {
  try {
    const res = await axiosInstance.get(`${environment.serverURL}/users/`)
    if (res.status === 200) {
      dispatch(setUsers(res.data));
    }
  } catch (error) {
    console.error(error);
  }
};

export const getUsers = (userId) => async (dispatch: Function) => {
  try {
    const res = await axiosInstance.get(`${environment.serverURL}/users/${userId}/users`)
    if (res.status === 200) {
      dispatch(setUsers(res.data));
    }
  } catch (error) {
    console.error(error);
  }
};

export const updateUserNotificationsSettings = (userId: string,selectedShop: string, email: string, iceCreamThreshold: number, packsThreshold: number) => async (dispatch: Function) => {
  try {
    const res = await axiosInstance.put(`${environment.serverURL}/users/${userId}/notifications`, { shop_id: selectedShop, email, ic_threshold: iceCreamThreshold, op_threshold: packsThreshold })
    if (res.status === 200) {
      dispatch(setIsSuccess(true))
    }
  } catch (error) {
    console.error(error);
  }
}

export const createUser = (user: any, userId: string) => async (dispatch: Function) => {
  try {
    dispatch(setIsLoading(true))
    const body = {
      email: user.email,
      name: user.name + ' ' + user.lastname,
      shops: user.shops,
      position: user.position
    }
    const res = await axiosInstance.post(`${environment.serverURL}/users/`, body)
    if (res.status === 201) {
      dispatch(getUsers(userId))
      dispatch(setIsSuccess(true))
      dispatch(setIsLoading(false))
    }
  } catch (error) {
    dispatch(setIsLoading(false))
    dispatch(setIsCreateUserError(true))
    console.error(error);
  }
};

export const updateUser = (user: any, userId: string) => async (dispatch: Function) => {
  try {
    const body = {
      email: user.email,
      name: user.name + ' ' + user.lastname,
      shops: user.shops,
      position: user.position
    }
    const res = await axiosInstance.put(`${environment.serverURL}/users/${user.id}`, body)
    if (res.status === 201) {
      dispatch(getUsers(userId))
    }
  } catch (error) {
    console.error(error);
  }
};

export const deleteUser = (userId: any) => async (dispatch: Function) => {
  try {
    const res = await axiosInstance.delete(`${environment.serverURL}/users/${userId}`)
    if (res.status === 200) {
      dispatch(getAllUsers())
    }
  } catch (error) {
    console.error(error);
  }
};

export const getUserNotifsConfig = (userId: string, selectedShop: string) => async (dispatch: Function) => {
  try {
    const res = await axiosInstance.get(`${environment.serverURL}/users/${userId}/notifications?shop_id=${selectedShop}`)
    if (res.status === 200) {
      dispatch(setUserNotifsConfig(res.data))
    }
  } catch (error) {
    console.error(error);
  }
}
export const { setUsers, setFetchingUsers, setSelectedShop, setUserNotifsConfig, setUserStripeSecretKey, setSubscriptionId, setIsLoading, setIsCreateUserError, setIsSuccess } = userSlice.actions;

export default userSlice.reducer;
